import { createSlice } from '@reduxjs/toolkit';
import {
  IReallocationHistory,
  IReallocationSlice,
  IReporteeWithAllocation
} from '../constants/reallocationInterfaces';
import { defaultPageOptionsAgents } from '../constants/reallocationConstants';

const getAutoCompleteOptions = (data: IReporteeWithAllocation[], isFromFelid?: boolean) => {
  if (!data) return [];
  const newData = data.map(reportee => {
    const { name, referenceId, activeUnpaidCases, currentLevel, bucketGroup } = reportee;
    return {
      label: name,
      value: referenceId,
      isDisabled: isFromFelid && (!activeUnpaidCases || activeUnpaidCases < 1),
      additionalData: { activeUnpaidCases, currentLevel, bucketGroup }
    };
  });
  return !isFromFelid ? newData.sort((a, b) => a.label.localeCompare(b.label)) : newData;
};

const initialState: IReallocationSlice = {
  reporteesWithAllocation: {
    data: [],
    originalData: [],
    selectedAgent: null,
    loading: false
  },
  activeTeleUsers: {
    data: [],
    maxCountOfAgents: defaultPageOptionsAgents.pageSize,
    loading: false
  },
  isFormSubmitting: false,
  reallocationHistory: {
    data: [],
    loading: false,
    totalElements: 0,
    pageNo: 0
  },
  reallocationCaseTypes: []
};

export const assignIndexToReallocationList = (reallocationHistory: IReallocationHistory[] = []) => {
  if (!reallocationHistory.length) {
    return [];
  }

  let activeRowIndex = -1; // Index of the row which is clicked

  return reallocationHistory?.map((reallocation, index) => {
    let currentRowIndex = index; // Index of the current row

    // If toggleState is true, then it means that the row is clicked
    if (reallocation.toggleState) {
      activeRowIndex = index;
    }

    // If the current row index is less than or equal to the clicked row index,
    // then increment the current row index by 1 to shift the index for the clicked row
    if (currentRowIndex <= activeRowIndex) {
      currentRowIndex = index + 1;
    }

    // Return the updated object with the currentRowIndex property
    return { ...reallocation, index: currentRowIndex };
  });
};

const reallocationSlice = createSlice({
  name: 'reallocation',
  initialState,
  reducers: {
    setReporteesWithAllocationLoading: (state, action) => {
      state.reporteesWithAllocation.loading = action.payload;
    },
    setReporteesWithAllocation: (state, action) => {
      const { data } = action.payload;
      const options = getAutoCompleteOptions(data, true);
      state.reporteesWithAllocation = {
        ...state.reporteesWithAllocation,
        data: options,
        originalData: data,
        loading: false
      };
    },
    setActiveTeleUsersLoading: (state, action) => {
      state.activeTeleUsers.loading = action.payload;
    },
    setActiveTeleUsers: (state, action) => {
      const { data } = action.payload;
      const options = getAutoCompleteOptions(data);
      state.activeTeleUsers = {
        ...state.activeTeleUsers,
        data: options,
        loading: false
      };
    },
    setIsFormSubmitting: (state, action) => {
      state.isFormSubmitting = action.payload;
    },
    setReallocationHistoryLoading: (state, action) => {
      state.reallocationHistory.loading = action.payload;
    },
    setReallocationHistory: (state, action) => {
      const { data, pages } = action.payload;
      state.reallocationHistory = {
        data: assignIndexToReallocationList(data),
        loading: false,
        totalElements: pages?.totalElements || 0,
        pageNo: pages?.pageNo + 1 || 1
      };
    },
    setReallocationHistoryDetailedView: (state, action) => {
      const data = [...state.reallocationHistory.data] as IReallocationHistory[];
      const clickedData = action.payload;

      let clickedIndex = data.findIndex(item => {
        return clickedData.referenceId === item.referenceId;
      });

      // If next row is detail row, remove it
      if (data[clickedIndex + 1]?.isDetailRow) {
        data.splice(clickedIndex + 1, 1);
        data[clickedIndex].toggleState = false;
        state.reallocationHistory = {
          ...state.reallocationHistory,
          data: assignIndexToReallocationList(data)
        };
        return;
      }

      // find detail row index
      const detailRowIndex = data.findIndex(item => {
        return item?.isDetailRow;
      });

      // If detail row exists, remove it
      if (detailRowIndex !== -1) {
        data[detailRowIndex - 1].toggleState = false;
        data.splice(detailRowIndex, 1);
        if (detailRowIndex < clickedIndex) {
          clickedIndex -= 1;
        }
      }

      // New detail row to be appended after the clicked row
      const newRow = {
        ...clickedData,
        isDetailRow: true,
        rowIndex: clickedIndex + 1
      };
      data.splice(clickedIndex + 1, 0, newRow);
      data[clickedIndex] = { ...data[clickedIndex], toggleState: true };
      state.reallocationHistory = {
        ...state.reallocationHistory,
        data: assignIndexToReallocationList(data)
      };
    },
    setSelectedAgent: (state, action) => {
      const selectedAgent = state.reporteesWithAllocation.originalData.find(
        agent => agent.referenceId === action.payload
      );
      if (selectedAgent) {
        state.reporteesWithAllocation.selectedAgent = selectedAgent;
        state.activeTeleUsers.maxCountOfAgents = selectedAgent.activeUnpaidCases;
      } else {
        state.reporteesWithAllocation.selectedAgent = null;
        state.activeTeleUsers.maxCountOfAgents = defaultPageOptionsAgents.pageSize;
      }
    },
    setCompletedReallocation: (state, action) => {
      const { data } = action.payload;
      const index = state.reallocationHistory.data?.findIndex(
        item => item.referenceId === data.referenceId
      );
      if (index !== -1) {
        state.reallocationHistory.data[index] = data;
      }
    },
    setReallocationCaseTypes: (state, action) => {
      state.reallocationCaseTypes = action.payload;
    }
  }
});

export const {
  setReporteesWithAllocation,
  setReporteesWithAllocationLoading,
  setActiveTeleUsers,
  setActiveTeleUsersLoading,
  setIsFormSubmitting,
  setReallocationHistory,
  setReallocationHistoryLoading,
  setReallocationHistoryDetailedView,
  setSelectedAgent,
  setCompletedReallocation,
  setReallocationCaseTypes
} = reallocationSlice.actions;

export default reallocationSlice.reducer;
